import PropTypes from 'prop-types'
import { useState, useEffect, Fragment, useMemo } from 'react'
import Spinner from 'react-bootstrap/Spinner'
import { useSelector } from 'react-redux'
import { aplicarFiltrosGenerico } from '../../helpers/filtros/aplicarFiltrosGenerico'
import { MAPA_PRESUPUESTOS } from '../../helpers/filtros/mapaPresupuestos.js'
import { useAuth } from '../../hooks/useAuth'
import { usePaginacion } from '../hooks/usePaginacion'
import ComponenteError from '../publicaciones/ComponenteError'
import { FiltrosContainer } from '../reutilizables/filtros/FiltrosContainer.jsx'
import { Paginacion } from '../reutilizables/paginacion/Paginacion'
import * as presupuestosService from '../servicios/presupuestosService'
import BordePrincipal from '../ui/BordePrincipal'
import Presupuesto from './Presupuesto'
import styles from './Presupuestos.module.css'

const ID_AVISO_COMPRADOR = 20
const ID_AVISO_VENDEDOR = 21
const PAGINA_ITEMS = 15

const Presupuestos = ({ esVendedor }) => {
  const { loginDataContext: { idUsuario, empid } = {}, esPerfilAdmin } = useAuth()
  const [presupuestos, setPresupuestos] = useState([])
  const [textoInput, setTextoInput] = useState('')
  const [textoBusqueda, setTextoBusqueda] = useState('')
  const [cargando, setCargando] = useState(true)
  const { filtrosLocales } = useSelector((state) => state.filters)

  const presupuestosFiltrados = useMemo(
    () =>
      aplicarFiltrosGenerico(presupuestos, { ...filtrosLocales, textoBusqueda }, MAPA_PRESUPUESTOS),
    [presupuestos, filtrosLocales, textoBusqueda]
  )
  const sinResultados = presupuestosFiltrados.length === 0

  const {
    itemsActual: presupuestosPaginados,
    paginaActual,
    setPaginaActual,
    itemsPorPagina,
    setItemsPorPagina,
    totalPaginas
  } = usePaginacion(presupuestosFiltrados, PAGINA_ITEMS)

  useEffect(() => {
    setPaginaActual(1)
  }, [presupuestosFiltrados.length])

  const fetchPresupuestos = async () => {
    const { data } = esPerfilAdmin
      ? await presupuestosService.buscarPorEmpresa(empid, esVendedor)
      : esVendedor
        ? await presupuestosService.buscarPorVendedor(idUsuario)
        : await presupuestosService.buscarPorComprador(idUsuario)
    setPresupuestos(data)
    setCargando(false)
  }

  useEffect(() => {
    if (!idUsuario || !empid) return

    setCargando(true)
    setTextoInput('')
    setTextoBusqueda('')
    fetchPresupuestos()
  }, [esVendedor, idUsuario, empid, esPerfilAdmin])

  const contextoFiltros = useMemo(() => ({ presupuestos }), [presupuestos])

  return (
    <BordePrincipal titulo="Mis Presupuestos">
      <div className={styles.layout}>
        <FiltrosContainer
          arrayAFiltrar={presupuestos}
          mapaFiltros={MAPA_PRESUPUESTOS}
          contextoFiltros={contextoFiltros}
          filtrosVisibles={[
            'idEstado',
            esVendedor ? 'empresaCompradora' : 'empresaVendedora',
            esPerfilAdmin ? (esVendedor ? 'vendedor' : 'comprador') : null
          ]}
          buscador={{
            onChange: setTextoBusqueda,
            placeholder: MAPA_PRESUPUESTOS.textoBusqueda.placeholder,
            valor: textoInput,
            setBusqueda: setTextoInput
          }}
        />
        <div className={`col ${styles.resultados}`}>
          {cargando ? (
            <div className="d-flex justify-content-center p-4">
              <Spinner animation="border" role="status" aria-label="Cargando" />
            </div>
          ) : !sinResultados ? (
            presupuestosPaginados.map((presupuesto) => (
              <Fragment key={presupuesto.idPresupuesto}>
                <Presupuesto presupuesto={presupuesto} esVentas={esVendedor} />
              </Fragment>
            ))
          ) : (
            <div className="row">
              <ComponenteError
                tipo="I"
                idAviso={esVendedor ? ID_AVISO_VENDEDOR : ID_AVISO_COMPRADOR}
              />
            </div>
          )}
        </div>
      </div>
      {totalPaginas > 1 && (
        <Paginacion
          totalPaginas={totalPaginas}
          paginaActual={paginaActual}
          onCambioPagina={setPaginaActual}
          itemsPorPagina={itemsPorPagina}
          onCambioItemsPorPagina={(num) => {
            setItemsPorPagina(num)
            setPaginaActual(1)
          }}
        />
      )}
    </BordePrincipal>
  )
}

Presupuestos.propTypes = {
  esVendedor: PropTypes.bool
}

export default Presupuestos
